import React, { memo, useCallback } from 'react'

import { Toggle } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  string, func, bool, node,
} from 'prop-types'

import InfoIconTooltip from 'Pods/ThreeDStudio/Shared/InfoIconTooltip/InfoIconTooltip'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

function ToggleSelector({
  id,
  keyName,
  sliceName,
  setFunction,
  label,
  disabled = false,
  tooltipMessage = undefined,
  badge = null,
  trailing = null,
}) {
  const isActive = useThreeDStudioStore(
    (state) => state[sliceName]?.[keyName] ?? state[keyName]
  )

  // setFunction must be in the deps: callers can pass a handler whose behavior
  // depends on state that hydrates after mount (e.g. PrivateModeSelector's
  // upsell handler reads userLoggedIn). Omitting it memoizes the first handler
  // and later clicks fire a stale closure.
  const handleToggle = useCallback(() => {
    setFunction(!isActive)
  }, [isActive, setFunction])

  // With a badge the label sits after the badge, so the Toggle renders switch-only
  // and the label is a sibling <label> kept associated via htmlFor.
  const toggleComponent = (
    <Toggle
      id={id}
      label={badge ? undefined : label}
      className="model-settings__toggle"
      isChecked={isActive}
      onChange={handleToggle}
      disabled={disabled}
    />
  )

  const infoTooltip = tooltipMessage && (
    <InfoIconTooltip
      id={`${id}-tooltip`}
      content={tooltipMessage}
      label={label}
      tooltipClassName="model-settings__tooltip"
      triggerClassName="model-settings__tooltip-trigger"
      place="top"
    />
  )

  if (!badge && !tooltipMessage && !trailing) {
    return toggleComponent
  }

  // Triangle's Toggle dims only the switch when disabled, so the container carries
  // the modifier that dims the label and any badge/trailing content with it.
  const containerClassName = [
    'model-settings__toggle-container',
    badge && 'model-settings__toggle-container--with-badge',
    trailing && 'model-settings__toggle-container--with-trailing',
    disabled && 'model-settings__toggle-container--disabled',
  ].filter(Boolean).join(' ')

  return (
    <div className={containerClassName}>
      {toggleComponent}
      {badge}
      {badge && (
        <label htmlFor={id} className="model-settings__toggle-label cgt-body-base-semibold">{label}</label>
      )}
      {trailing}
      {infoTooltip}
    </div>
  )
}

ToggleSelector.propTypes = {
  id: string.isRequired,
  keyName: string.isRequired,
  sliceName: string.isRequired,
  setFunction: func.isRequired,
  label: string.isRequired,
  disabled: bool,
  tooltipMessage: string,
  badge: node,
  trailing: node,
}

export default memo(ToggleSelector)
